import React, { Fragment, useEffect, useState } from 'react';
import T from 'prop-types';
// redux
import { setPageOptions } from 'reducers/layout';
import { setBreadcrumbs } from 'reducers/breadcrumbs';
// components
import { FormattedMessage } from 'react-intl';
import Preloader from 'components/Preloader';
import TreatmentProtocolForm from '../../components/TreatmentProtocolForm';
// endpoints
import { getPigGroupTreatmentProtocol, updatePigGroupTreatmentProtocol } from 'endpoints/treatmentProtocols';
// utils
import { connect } from 'react-redux';
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import {
  formatTreatmentProtocolPayload,
  formatTreatmentProtocolResponse,
  markDestroyedItems,
  validateUpdatedTreatmentProtocol
} from '../../helper';

const EditGroupTreatmentProtocol = ({
  pigGroup,
  setBreadcrumbs,
  setPageOptions,
  router: {
    params: {
      farmId,
      groupId,
      protocolId,
    },
    push,
  },
  location: { query },
}) => {
  // Opened from a combined entry of identical copies: the edit reaches all of them
  const isCombinedEdit = query?.sync_combined === 'true';
  const [isLoading, setIsLoading] = useState(false);
  const [treatmentProtocol, setTreatmentProtocol] = useState(undefined);

  const fetchData = async () => {
    try {
      const fetchedTreatmentProtocol = await getPigGroupTreatmentProtocol(groupId, protocolId);
      const formattedTreatmentProtocol = formatTreatmentProtocolResponse(fetchedTreatmentProtocol);
      setTreatmentProtocol(formattedTreatmentProtocol);
    } catch (error) {
      toastResponseErrors(error);
      push(`/treatment-protocols-farms/${farmId}/groups/${groupId}/treatment-protocols`);
    }
  };

  useEffect(() => {
    fetchData();
    setPageOptions({ backLink: `/treatment-protocols-farms/${farmId}/groups/${groupId}/treatment-protocols` });
  }, []);

  useEffect(() => {
    setBreadcrumbs([
      {
        label: <FormattedMessage id="general.treatmentProtocolFarms" />,
        path: '/treatment-protocols-farms',
      },
      {
        label: pigGroup?.farm?.name || '',
        path: `/treatment-protocols-farms/${farmId}/groups`,
      },
      {
        label: pigGroup?.name || '',
        path: `/treatment-protocols-farms/${farmId}/groups/${groupId}/treatment-protocols`,
      },
      {
        label: treatmentProtocol?.name || '',
        useLabelAsMobileTitle: true,
      },
    ]);
  }, [pigGroup?.name, treatmentProtocol?.name]);

  const onSubmit = async (updatedTreatmentProtocol) => {
    try {
      setIsLoading(true);
      const markedTreatmentProtocolPayload = markDestroyedItems(updatedTreatmentProtocol, treatmentProtocol);
      const treatmentProtocolPayload = formatTreatmentProtocolPayload(markedTreatmentProtocolPayload);
      await updatePigGroupTreatmentProtocol(
        groupId,
        protocolId,
        treatmentProtocolPayload,
        isCombinedEdit ? { sync_combined: true } : {},
      );
      push(`/treatment-protocols-farms/${farmId}/groups/${groupId}/treatment-protocols`);
    } catch (error) {
      toastResponseErrors(error);
      setIsLoading(false);
      // The copies can no longer be edited together: back to the list, which offers them one by one
      if (isCombinedEdit && typeof error.error === 'string') {
        push(`/treatment-protocols-farms/${farmId}/groups/${groupId}/treatment-protocols`);
      }
    }
  };

  if (!treatmentProtocol) return <Preloader isActive />;

  const isProtocolInheritedFromFarm = !!treatmentProtocol.farm_treatment_protocol;

  return (
    <Fragment>
      <Preloader isActive={isLoading} />
      <TreatmentProtocolForm
        isBaseDateDisabled
        isNameChangeable={!isProtocolInheritedFromFarm}
        isProductsChangeable={!isProtocolInheritedFromFarm && !isCombinedEdit}
        isEffectiveDatesReadOnly={isProtocolInheritedFromFarm}
        validate={validateUpdatedTreatmentProtocol}
        onSubmit={onSubmit}
        initialValues={treatmentProtocol}
        title={<FormattedMessage id="general.editTreatmentProtocol" />}
        submitLabel={<FormattedMessage id="general.updateTreatmentProtocol" />}
        ABFEnabled={pigGroup.abf_tracking}
      />
    </Fragment>
  );
};

EditGroupTreatmentProtocol.propTypes = {
  setBreadcrumbs: T.func.isRequired,
  setPageOptions: T.func.isRequired,
  pigGroup: T.object.isRequired,
  router: T.object.isRequired,
  location: T.object.isRequired,
};

export default connect(
  (state) => ({
    pigGroup: state.profiles.pigGroup.data,
  }), {
    setPageOptions,
    setBreadcrumbs,
  }
)(EditGroupTreatmentProtocol);
